import React, { useEffect, useState, useRef, useMemo } from "react";
import { X, Upload, Trash2, Check, ImageIcon, Loader2, Search, FolderPlus, Folder } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const toAbsolute = (url) => (url && url.startsWith("/api/") ? `${BACKEND}${url}` : url);

export default function ImageLibrary({ open, onClose, onSelect, currentUrl }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [activeFolder, setActiveFolder] = useState("All");
  const fileRef = useRef(null);

  const load = () => { setLoading(true); api.get("/admin/images").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { if (open) { load(); setQuery(""); setActiveFolder("All"); } }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const folders = useMemo(() => {
    const s = new Set(items.map((i) => i.folder || "General"));
    return ["All", ...Array.from(s).sort()];
  }, [items]);

  const visible = useMemo(() => items.filter((i) => {
    const inFolder = activeFolder === "All" || (i.folder || "General") === activeFolder;
    const match = (i.original_filename || "").toLowerCase().includes(query.toLowerCase());
    return inFolder && match;
  }), [items, activeFolder, query]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", activeFolder === "All" ? "General" : activeFolder);
    try {
      const { data } = await api.post("/admin/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded to ${data.folder || "General"}`);
      setItems((prev) => [data, ...prev]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const newFolder = () => {
    const name = window.prompt("New folder name");
    if (name && name.trim()) setActiveFolder(name.trim());
  };

  const remove = async (id) => {
    await api.delete(`/admin/images/${id}`);
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-primary/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="image-library">
      <div className="bg-background rounded-2xl max-w-3xl w-full max-h-[85vh] flex flex-col p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-semibold text-primary flex items-center gap-2"><ImageIcon size={20} className="text-secondary" /> Image Library</h2>
          <div className="flex items-center gap-2">
            <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="library-upload-btn" className="flex items-center gap-2 rounded-full bg-secondary text-secondary-foreground px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-60">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="library-file-input" />
            <button onClick={onClose}><X className="text-primary/60" /></button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary/40" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by file name…" data-testid="library-search"
              className="w-full rounded-full border border-input bg-background pl-9 pr-3 py-2 text-sm" />
          </div>
          <button onClick={newFolder} data-testid="library-new-folder" className="flex items-center gap-1.5 rounded-full border border-primary/20 text-primary px-3 py-2 text-sm font-semibold hover:bg-primary/5">
            <FolderPlus size={15} /> New folder
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {folders.map((f) => (
            <button key={f} onClick={() => setActiveFolder(f)} data-testid={`library-folder-${f}`}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${activeFolder === f ? "bg-primary text-primary-foreground" : "border border-primary/20 text-primary hover:bg-primary/5"}`}>
              {f !== "All" && <Folder size={12} />} {f}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto no-scrollbar">
          {loading ? (
            <p className="text-muted-foreground py-10 text-center flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</p>
          ) : visible.length === 0 ? (
            <p className="text-muted-foreground py-10 text-center">{items.length === 0 ? "No images yet. Upload one to get started." : "No images match your search."}</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {visible.map((img) => {
                const abs = toAbsolute(img.url);
                const inUse = currentUrl && (currentUrl === abs || currentUrl === img.url);
                return (
                  <div key={img.id} data-testid={`library-item-${img.id}`}
                    className={`group relative rounded-xl overflow-hidden border aspect-square bg-muted ${inUse ? "border-secondary ring-2 ring-secondary" : "border-primary/10"}`}>
                    <img src={abs} alt={img.original_filename}
                      onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                      className="w-full h-full object-cover" />
                    <div style={{ display: "none" }} className="absolute inset-0 flex-col items-center justify-center gap-1 text-primary/40 bg-muted p-2 text-center">
                      <ImageIcon size={22} />
                      <span className="text-[10px] leading-tight break-all">{img.original_filename || "image"}</span>
                    </div>
                    {inUse && <span className="absolute top-1.5 left-1.5 z-10 bg-secondary text-secondary-foreground text-[10px] font-semibold px-2 py-0.5 rounded-full">In use</span>}
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-primary/70 to-transparent px-2 py-1.5 pt-5">
                      <p className="text-[10px] text-white truncate">{img.original_filename || "image"}</p>
                    </div>
                    <div className="absolute inset-0 bg-primary/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button onClick={() => onSelect(abs)} data-testid={`library-pick-${img.id}`} className="w-9 h-9 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center"><Check size={16} /></button>
                      <button onClick={() => remove(img.id)} className="w-9 h-9 rounded-full bg-background text-destructive flex items-center justify-center"><Trash2 size={15} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
